import {
  Children,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils.js';
import type { SelectOptionItem } from '../../locale-region-options.js';
import { Button } from './primitives.js';

let activeDropdownDismiss: (() => void) | null = null;

/**
 * Ensures at most one dropdown / popover menu can be open across the UI at any time.
 * When a new dropdown opens, any previously registered dropdown is immediately closed.
 */
export function useExclusiveDropdownClose(open: boolean, close: () => void): void {
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;
    const dismissThis = (): void => {
      closeRef.current();
    };
    if (activeDropdownDismiss !== null && activeDropdownDismiss !== dismissThis) {
      activeDropdownDismiss();
    }
    activeDropdownDismiss = dismissThis;
    return () => {
      if (activeDropdownDismiss === dismissThis) {
        activeDropdownDismiss = null;
      }
    };
  }, [open]);
}

function computeDropdownPopoverStyle(el: HTMLElement | null): CSSProperties {
  if (!el || typeof window === 'undefined') return {};
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return {};

  const viewportW = window.innerWidth || 1024;
  const viewportH = window.innerHeight || 768;
  const margin = 12;
  const maxPopoverWidth = Math.min(380, Math.max(200, viewportW - margin * 2));
  const minPopoverWidth = Math.min(Math.max(rect.width, 220), maxPopoverWidth);
  const overflowsRight = rect.left + maxPopoverWidth > viewportW - margin;
  const canAlignRight = rect.right >= minPopoverWidth + margin;

  const horizontalStyle: CSSProperties =
    overflowsRight && canAlignRight
      ? { right: Math.max(margin, viewportW - rect.right) }
      : { left: Math.max(margin, Math.min(rect.left, viewportW - minPopoverWidth - margin)) };

  const spaceBelow = viewportH - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const openUpward = spaceBelow < 240 && spaceAbove > spaceBelow;
  const verticalStyle: CSSProperties = openUpward
    ? { bottom: Math.max(margin, viewportH - rect.top + 6) }
    : { top: Math.max(margin, rect.bottom + 6) };

  return {
    position: 'fixed',
    zIndex: 80,
    minWidth: `${Math.round(rect.width)}px`,
    maxWidth: `${Math.round(maxPopoverWidth)}px`,
    ...horizontalStyle,
    ...verticalStyle,
  };
}

export function useDropdownPopoverPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!open || typeof window === 'undefined') {
      setStyle({});
      return;
    }
    const computePosition = (): void => {
      setStyle(computeDropdownPopoverStyle(triggerRef.current));
    };
    computePosition();
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [open, triggerRef]);

  if (!open) return {};
  return style.position !== undefined ? style : computeDropdownPopoverStyle(triggerRef.current);
}

export interface ParsedSelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled: boolean;
}

function extractTextFromNode(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractTextFromNode).join('');
  if (isValidElement<{ readonly children?: ReactNode }>(node)) {
    return extractTextFromNode(node.props.children);
  }
  return '';
}

export function parseSelectOptionChildren(children: ReactNode): readonly ParsedSelectOption[] {
  const options: ParsedSelectOption[] = [];
  Children.forEach(children, (child) => {
    if (
      !isValidElement<{
        readonly value?: string | number;
        readonly disabled?: boolean;
        readonly children?: ReactNode;
      }>(child)
    ) {
      return;
    }
    if (child.type === 'option') {
      const label = extractTextFromNode(child.props.children).trim();
      const rawVal = child.props.value !== undefined ? String(child.props.value) : label;
      options.push({
        value: rawVal,
        label: label.length > 0 ? label : rawVal,
        disabled: Boolean(child.props.disabled),
      });
    } else if (child.props.children !== undefined) {
      options.push(...parseSelectOptionChildren(child.props.children));
    }
  });
  return options;
}

export interface SearchableMultiSelectProps {
  readonly id?: string;
  readonly value: string;
  readonly onChange: (nextCommaSeparatedValue: string) => void;
  readonly options: readonly SelectOptionItem[];
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly disabled?: boolean;
  readonly className?: string;
}

function parseCommaSeparatedTokens(raw: string): readonly string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of raw.split(',')) {
    const trimmed = part.trim();
    if (trimmed.length > 0 && !seen.has(trimmed)) {
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result;
}

/**
 * Searchable multi-select popover dropdown that stores and emits a comma-separated
 * string of selected option values (e.g., `"en, fr"` or `"GB, EU"`).
 */
export function SearchableMultiSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select options...',
  searchPlaceholder = 'Search...',
  disabled = false,
  className,
}: SearchableMultiSelectProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const popoverStyle = useDropdownPopoverPosition(open, triggerRef);

  useExclusiveDropdownClose(open, () => {
    setOpen(false);
    setSearchQuery('');
  });

  const selectedValues = useMemo(() => parseCommaSeparatedTokens(value), [value]);

  const effectiveOptions = useMemo(() => {
    const known = new Set(options.map((o) => o.value));
    const extras: SelectOptionItem[] = [];
    for (const val of selectedValues) {
      if (!known.has(val)) {
        extras.push({ value: val, label: val });
      }
    }
    return extras.length > 0 ? [...extras, ...options] : options;
  }, [options, selectedValues]);

  const optionLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of effectiveOptions) {
      map.set(opt.value, opt.label);
    }
    return map;
  }, [effectiveOptions]);

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length === 0) return effectiveOptions;
    return effectiveOptions.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [effectiveOptions, searchQuery]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const handleMouseDown = (event: MouseEvent): void => {
      if (
        event.target instanceof Node &&
        !containerRef.current?.contains(event.target) &&
        !popoverRef.current?.contains(event.target)
      ) {
        setOpen(false);
        setSearchQuery('');
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open]);

  const handleToggleOption = (optionValue: string): void => {
    const exists = selectedValues.includes(optionValue);
    let next: string[];
    if (exists) {
      next = selectedValues.filter((v) => v !== optionValue);
      if (next.length === 0) {
        next = [optionValue];
      }
    } else {
      next = [...selectedValues, optionValue];
    }
    onChange(next.join(', '));
  };

  const summaryText =
    selectedValues.length === 0
      ? placeholder
      : selectedValues.map((v) => optionLabelMap.get(v) ?? v).join(', ');

  return (
    <div ref={containerRef} className="relative min-w-0">
      <Button
        ref={triggerRef}
        id={id}
        type="button"
        variant="ghost"
        size="inline"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
            setSearchQuery('');
          }
        }}
        className={cn(
          'box-border flex h-[36px] min-h-[36px] max-h-[36px] w-full max-w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-control border px-3 py-0 text-left text-[13px] font-medium leading-none transition-colors focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-wash disabled:cursor-not-allowed disabled:opacity-50',
          open
            ? 'border-brand bg-surface text-fg shadow-card-base'
            : 'border-border bg-surface-subtle text-fg hover:border-border-medium hover:bg-surface',
          className,
        )}
      >
        <span className="truncate">{summaryText}</span>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={cn(
            'shrink-0 text-fg-secondary transition-transform duration-150',
            open && 'rotate-180 text-brand',
          )}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </Button>

      {open &&
        (() => {
          const popoverEl = (
            <div
              ref={popoverRef}
              role="listbox"
              aria-multiselectable="true"
              style={popoverStyle}
              className={cn(
                'w-max overflow-hidden rounded-card border border-border bg-surface shadow-modal',
                popoverStyle.position === 'fixed'
                  ? ''
                  : 'absolute left-0 top-full z-50 mt-1.5 min-w-full max-w-[min(380px,calc(100vw-2.5rem))]',
              )}
            >
              <div className="border-b border-subtle p-2">
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  className="h-8 w-full rounded-control border border-border bg-surface-subtle px-2.5 text-[12px] text-fg placeholder:text-fg-muted focus:border-brand focus:bg-surface focus:outline-none"
                />
              </div>

              <div className="max-h-64 overflow-y-auto p-1.5">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-[12px] text-fg-muted">No matching options</div>
                ) : (
                  filteredOptions.map((opt) => {
                    const isSelected = selectedValues.includes(opt.value);
                    return (
                      <Button
                        key={opt.value}
                        type="button"
                        variant="ghost"
                        size="inline"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleToggleOption(opt.value)}
                        className={cn(
                          'flex min-h-[34px] w-full cursor-pointer items-center justify-between gap-3 rounded-[5px] px-3 py-2 text-left text-[13px] transition-colors',
                          isSelected
                            ? 'bg-brand-wash font-semibold text-brand'
                            : 'font-medium text-fg hover:bg-surface-subtle',
                        )}
                      >
                        <span className="truncate">{opt.label}</span>
                        {isSelected && (
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            className="shrink-0 text-brand"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        )}
                      </Button>
                    );
                  })
                )}
              </div>
            </div>
          );
          return popoverStyle.position === 'fixed' && typeof document !== 'undefined'
            ? createPortal(popoverEl, document.body)
            : popoverEl;
        })()}
    </div>
  );
}
